// Üzenet-ÍRÁS — jobb oldali beúszó FormSheet (az asset-request-sheet create-mintája). A compose EGYETLEN
// POST /messages hívás: a 3 lépcsős busz-orchestrációt (draft → címzett-pivotok → záró státusz-PATCH) a BFF
// végzi — a kliens a teljes szándékot (tartalom + típus + címzés + küldés-mód) egy body-ban adja fel.
// Címzés: a típus (internal/external) és a címzés-kind KÖLCSÖNÖSEN kizáró SZEGMENTÁLT választók. A címzés-fajta
// ELŐVÁLASZTÁS NÉLKÜL indul (kolléga-észrevétel: az alapból kijelölt „Mindenki" mellett egy elfelejtett átállítás
// az egész cégnek ment) — választás nélkül mező-hiba; a „Mindenki" / „Minden ügyfél" küldése előtt MEGERŐSÍTÉS kér
// a címzett-darabszámmal. Célzott kindnél multiselect a /messages/refs opcióiból, ÜRES célzás → kliens-oldali
// mező-hiba (a szerver 422-je a fal).
// Küldés-mód: „Küldés" (most) vagy „Időzítés" (datetime-local, HELYI idő → UTC ISO a wire-en). Hiba (pl.
// 502 részleges írás) → a sheet nyitva marad, a form-állapot megmarad (a user újraküldhet / a draft-sor a
// listából folytatható). Nincs Idempotency-Key (az internal busz elutasítja) — a dupla-submitet a FormSheet
// `busy` állapota védi.
import { type MessageRefsSchema } from '@atlas/contracts';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { type z } from 'zod';

import { MessageContentFields } from '@/components/messages/message-content-fields';
import { MessageTargetMultiSelect } from '@/components/messages/message-target-multiselect';
import { DraftStatus } from '@/components/shared/draft-status';
import { DateTimeField, SegmentedField } from '@/components/shared/form-fields';
import { FormSheet } from '@/components/shared/form-sheet';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { PendingAttachmentsField } from '@/components/shared/pending-attachments-field';
import { Button } from '@/components/ui/button';
import { Form, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { messageKeys } from '@/features/messages/api';
import {
  MAX_ATTACHMENTS,
  uploadMessageAttachment,
  type AttachmentUploadOutcome,
} from '@/features/messages/attachment-upload';
import {
  buildMessageComposeResolver,
  EMPTY_MESSAGE_COMPOSE,
  isBroadAudience,
  isComposeEmpty,
  kindNeedsIds,
  kindsForType,
  refsKeyForKind,
  toComposeBody,
  type MessageComposeValues,
} from '@/features/messages/form';
import { useDraft } from '@/hooks/use-draft';
import { api } from '@/lib/api';
import { toPersonOptions } from '@/lib/person-options';
import { useEntityMutation } from '@/lib/use-entity-mutation';

type MessageRefs = z.infer<typeof MessageRefsSchema>;

/** A sikertelen feltöltés OKA (a kimenet → a meglévő toast-szöveg) — a „nem sikerült" mellé. */
const UPLOAD_FAILURE_REASON: Record<Exclude<AttachmentUploadOutcome, 'ok'>, string> = {
  forbidden: 'toast:messages.forbidden',
  validation: 'toast:messages.attachmentValidation',
  unavailable: 'toast:messages.attachmentUnavailable',
  failed: 'toast:messages.attachmentError',
};

export interface MessageComposeSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function MessageComposeSheet({ open, onOpenChange }: MessageComposeSheetProps): JSX.Element {
  const { t, i18n } = useTranslation();

  // Címzett-picker opciók (messages.manage-gated endpoint — csak a compose kéri); csak nyitva töltjük.
  const refsQ = api.messageRefs.useQuery(
    messageKeys.refs(),
    {},
    { queryKey: messageKeys.refs(), enabled: open },
  );
  const refs: MessageRefs | undefined = refsQ.data?.status === 200 ? refsQ.data.body : undefined;

  const form = useForm<MessageComposeValues>({
    resolver: buildMessageComposeResolver(t),
    mode: 'onTouched', // validáció már mentés előtt: a mezőből kilépve (blur), utána élőben
    defaultValues: EMPTY_MESSAGE_COMPOSE,
  });
  const { reset, handleSubmit, setError, setValue, watch, clearErrors } = form;
  /**
   * A csatolandó fájlok. A melléklet-API üzenethez kötött (`/messages/:id/attachments`), a formon viszont még
   * nincs id → a fájlok az üzenet LÉTREJÖTTE UTÁN mennek fel (a jegy-nyitás mintája). Ha egy fájl elhasal, az
   * üzenet attól még elkészült/elment, és a hiba NÉV SZERINT jelzett.
   */
  const [files, setFiles] = useState<File[]>([]);

  // AUTOMATIKUS VÁZLAT: a félbehagyott üzenet (tárgy, törzs, címzés, linkek, időzítés) egy bezárást / frissítést
  // túlél; küldés vagy „Vázlat elvetése" után törlődik. A mellékletek NEM (fájl nem tárolható a böngészőben).
  // a munkamenet KÉSŐI betöltődésekor csak egy létező vázlat ír az űrlapra (a begépelt szöveg nem vész el)
  const draft = useDraft<MessageComposeValues>('message-compose', {
    onRestore: (value) => {
      if (value) reset({ ...EMPTY_MESSAGE_COMPOSE, ...value });
    },
  });
  const { save: saveDraft } = draft;
  const restoreRef = useRef(draft.restore);
  restoreRef.current = draft.restore;
  // MEGNYITÁSKOR áll be az űrlap (a vázlatra vagy üresre) — NEM bezáráskor: a bezáráskori üresítés a
  // változás-figyelőn át a vázlatot is törölné. CSAK a megnyitásra fut (a ref miatt nem a kulcs-váltásra).
  useEffect(() => {
    if (!open) return;
    reset({ ...EMPTY_MESSAGE_COMPOSE, ...(restoreRef.current() ?? {}) });
  }, [open, reset]);
  useEffect(() => {
    if (!open) return undefined;
    const sub = watch((values) => {
      saveDraft(values as MessageComposeValues, isComposeEmpty(values));
    });
    return () => sub.unsubscribe();
  }, [open, watch, saveDraft]);

  const type = watch('type');
  const audienceKind = watch('audienceKind');
  const deliveryMode = watch('deliveryMode');

  // Típus-váltás → a kind VÁLASZTATLANRA áll (nincs előválasztott „mindenki"), a célzott lista ürül.
  useEffect(() => {
    if (audienceKind !== '' && !(kindsForType(type) as readonly string[]).includes(audienceKind)) {
      setValue('audienceKind', '', { shouldValidate: false });
      setValue('targetIds', [], { shouldValidate: false });
      clearErrors('targetIds');
    }
  }, [type, audienceKind, setValue, clearErrors]);

  const mutation = useEntityMutation<MessageComposeValues>({
    mutationFn: (values) => api.messageCreate.mutation({ body: toComposeBody(values) }),
    invalidateKeys: [messageKeys.lists()],
    onFieldErrors: (field, messages) => {
      // A szerver 422-je a wire-mezőkre jön; a compose-only pathok (audience/delivery) a saját mezőjükre.
      if (field in EMPTY_MESSAGE_COMPOSE) {
        setError(field as keyof MessageComposeValues, { message: messages.join(' ') });
      } else if (field === 'audience') {
        setError('targetIds', { message: messages.join(' ') });
      } else if (field === 'delivery' || field === 'scheduledAt') {
        setError('scheduledAtLocal', { message: messages.join(' ') });
      }
    },
    messages: {
      success:
        deliveryMode === 'schedule' ? t('toast:messages.scheduled') : t('toast:messages.sent'),
      conflict: t('toast:messages.conflict'),
      forbidden: t('toast:messages.forbidden'),
      validation: t('toast:messages.validation'),
      error: t('toast:messages.error'),
    },
  });

  /**
   * A SZÉLES címzés („Mindenki" / „Minden ügyfél") küldése előtt megerősítés — a validált értékek addig itt
   * várnak. A darabszám a compose-picker listájából jön (a szerver ugyanebből bontja ki a széles címzést).
   */
  const [pendingBroad, setPendingBroad] = useState<MessageComposeValues | null>(null);

  const send = (values: MessageComposeValues): void => {
    mutation.mutate(values, {
      onSuccess: (res) => {
        // AZ ÜZENET MEGVAN → a mellékletek utána mennek fel (az üzenet id-jére). A hook `body`-ja generikus
        // `unknown` (nem üzenet-specifikus mutation-eredmény) → szűkítjük.
        const body = res.body as { id?: unknown };
        const messageId = typeof body.id === 'string' ? body.id : '';
        const pending = files;
        draft.discard();
        reset(EMPTY_MESSAGE_COMPOSE);
        setFiles([]);
        onOpenChange(false);
        if (messageId === '' || pending.length === 0) return;
        void (async () => {
          const failed: string[] = [];
          let firstFailure: AttachmentUploadOutcome | null = null;
          for (const file of pending) {
            const outcome = await uploadMessageAttachment(messageId, file);
            if (outcome !== 'ok') {
              failed.push(file.name);
              firstFailure ??= outcome;
            }
          }
          if (failed.length > 0) {
            // az OK is látszik (jog · feltétel · beállítás · átmeneti), nem csak az, hogy „nem sikerült"
            toast.error(t('toast:messages.attachmentPartial', { files: failed.join(', ') }), {
              description: t(UPLOAD_FAILURE_REASON[firstFailure ?? 'failed']),
            });
          } else {
            toast.success(t('toast:messages.attachmentUploaded'));
          }
        })();
      },
    });
  };

  const onSubmit = handleSubmit((values) => {
    if (isBroadAudience(values.audienceKind)) {
      setPendingBroad(values);
      return;
    }
    send(values);
  });

  const broadKind =
    pendingBroad !== null && isBroadAudience(pendingBroad.audienceKind)
      ? pendingBroad.audienceKind
      : null;
  const broadCount =
    broadKind === null || !refs
      ? null
      : broadKind === 'everyone'
        ? refs.employees.length
        : refs.contacts.length;

  // SZEGMENTÁLT választók (nem telített zöld pillek — user-döntés): a típus és a címzés-fajta
  // SEMLEGES dimenzió, nincs saját jelentés-színe. A telített zöld az „elsődleges akció" hangsúlyát vitte rá.
  const typeOptions = (['internal', 'external'] as const).map((v) => ({
    value: v,
    label: t(`messages.typeLabel.${v}`),
  }));
  const kindOptions = kindsForType(type).map((k) => ({
    value: k,
    label: t(`messages.audienceKind.${k}`),
  }));

  const refsKey = refsKeyForKind(audienceKind);
  const rawTargets = refsKey && refs ? refs[refsKey] : [];
  // A SZEMÉLY-dimenziók (kollégák/kapcsolattartók) nyelvhelyes névsorrendet és ABC-rendet kapnak; a többi
  // dimenzió (részleg/fiók/szolgáltatás) neve nem személy-név, ott a szerver rendezése a helyes.
  const targetOptions =
    refsKey === 'employees' || refsKey === 'contacts'
      ? toPersonOptions(rawTargets, i18n.language)
      : rawTargets;

  // Az „Időzítés…" láb-gomb váltja a küldés-módot; schedule-módban jelenik meg a datetime-mező.
  const toggleSchedule = (): void => {
    const next = deliveryMode === 'send' ? 'schedule' : 'send';
    setValue('deliveryMode', next, { shouldValidate: false });
    if (next === 'send') {
      setValue('scheduledAtLocal', '', { shouldValidate: false });
      clearErrors('scheduledAtLocal');
    }
  };

  return (
    <>
      <FormSheet
        open={open}
        onOpenChange={(o) => {
          // a szöveg a vázlatban marad (a következő megnyitás visszatölti); a fájlok nem tárolhatók
          if (!o) setFiles([]);
          onOpenChange(o);
        }}
        title={t('messages.compose.title')}
        description={t('messages.compose.desc')}
        onSubmit={onSubmit}
        busy={mutation.isPending}
        footer={
          <>
            {/* a vázlat-jelzés balra; az elvetés az űrlapot is üríti (a mellékletekkel együtt) */}
            <div className="mr-auto">
              <DraftStatus
                savedAt={draft.savedAt}
                onDiscard={() => {
                  draft.discard();
                  reset(EMPTY_MESSAGE_COMPOSE);
                  setFiles([]);
                }}
                disabled={mutation.isPending}
              />
            </div>
            <Button
              variant="outline"
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              {t('messages.cancel')}
            </Button>
            <Button
              variant="outline"
              type="button"
              onClick={toggleSchedule}
              disabled={mutation.isPending}
            >
              {deliveryMode === 'send'
                ? t('messages.compose.scheduleToggle')
                : t('messages.compose.scheduleCancel')}
            </Button>
            <Button type="submit" variant="brand" disabled={mutation.isPending}>
              {mutation.isPending
                ? t('messages.compose.submitting')
                : deliveryMode === 'schedule'
                  ? t('messages.compose.submitSchedule')
                  : t('messages.compose.submitSend')}
            </Button>
          </>
        }
      >
        <Form {...form}>
          <SegmentedField<MessageComposeValues>
            name="type"
            label={t('messages.fields.type')}
            required
            options={typeOptions}
          />
          <SegmentedField<MessageComposeValues>
            name="audienceKind"
            label={t('messages.fields.audience')}
            required
            options={kindOptions}
            {...(isBroadAudience(audienceKind)
              ? { hint: t(`messages.compose.flagHint.${audienceKind}`) }
              : {})}
          />
          {kindNeedsIds(audienceKind) && (
            <FormField<MessageComposeValues>
              name="targetIds"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t(`messages.compose.targetsLabel.${audienceKind}`)}
                    <span className="text-destructive"> *</span>
                  </FormLabel>
                  <MessageTargetMultiSelect
                    options={targetOptions}
                    value={Array.isArray(field.value) ? field.value : []}
                    onChange={(ids) => field.onChange(ids)}
                    loading={refsQ.isLoading}
                    aria-label={t(`messages.compose.targetsLabel.${audienceKind}`)}
                  />
                  {/* A RÉSZLEG-célzás a csapatain KERESZTÜL ér el mindenkit (a részlegnek nincs saját tagsága —
                    a hierarchia: részleg → csapat → beosztás) — ez a UI-ban NEM magától értetődő, ezért itt
                    kimondjuk (i18n-kulcsból, nem hardcode). */}
                  {audienceKind === 'departments' && (
                    <p className="text-subtle text-xs">{t('messages.compose.departmentsHint')}</p>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
          <MessageContentFields />
          {/* Mellékletek — a legacy írás-formján is volt; a fájlok az üzenet létrejötte UTÁN mennek fel. */}
          <PendingAttachmentsField
            files={files}
            onChange={setFiles}
            disabled={mutation.isPending}
            labels={{
              title: t('messages.attachments.title'),
              add: t('messages.attachments.add'),
              remove: (name) => t('messages.attachments.remove', { name }),
            }}
            messages={{
              limit: t('toast:messages.attachmentLimit', { max: MAX_ATTACHMENTS }),
              invalidType: t('toast:messages.attachmentInvalidType'),
              tooLarge: t('toast:messages.attachmentTooLarge'),
            }}
          />
          {deliveryMode === 'schedule' && (
            <DateTimeField<MessageComposeValues>
              name="scheduledAtLocal"
              label={t('messages.fields.scheduledAt')}
              required
              hint={t('messages.fields.scheduledAtHint')}
            />
          )}
        </Form>
      </FormSheet>
      <ConfirmDialog
        open={pendingBroad !== null}
        onOpenChange={(o) => {
          if (!o) setPendingBroad(null);
        }}
        tone="warning"
        title={broadKind === null ? '' : t(`messages.compose.confirmBroad.title.${broadKind}`)}
        description={
          broadKind === null
            ? ''
            : broadCount === null
              ? t(`messages.compose.confirmBroad.descNoCount.${broadKind}`)
              : t(`messages.compose.confirmBroad.desc.${broadKind}`, { count: broadCount })
        }
        confirmLabel={
          pendingBroad?.deliveryMode === 'schedule'
            ? t('messages.compose.submitSchedule')
            : t('messages.compose.submitSend')
        }
        cancelLabel={t('messages.compose.confirmBroad.back')}
        onConfirm={() => {
          const values = pendingBroad;
          setPendingBroad(null);
          if (values !== null) send(values);
        }}
      />
    </>
  );
}
